import React, { useCallback, useEffect, useRef, useState } from 'react';
import fetchMethodRequest from '../../../../config/service';
import apiCalls from '../../../../config/apiCalls';
import { marked } from 'marked';
import { Button } from 'primereact/button';

const FILE_TYPE_ICONS = {
  pdf: { icon: 'pi pi-file-pdf', color: '#ef4444' },
  docx: { icon: 'pi pi-file-word', color: '#2563eb' },
  doc: { icon: 'pi pi-file-word', color: '#2563eb' },
  xlsx: { icon: 'pi pi-file-excel', color: '#16a34a' },
  xls: { icon: 'pi pi-file-excel', color: '#16a34a' },
  txt: { icon: 'pi pi-file', color: '#64748b' },
  default: { icon: 'pi pi-file', color: '#64748b' },
};

function getFileTypeInfo(fileName = '') {
  const ext = (fileName.split('.').pop() || '').toLowerCase();
  return FILE_TYPE_ICONS[ext] || FILE_TYPE_ICONS.default;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function truncate(str = '', max = 36) {
  return str.length > max ? str.substring(0, max - 3) + '...' : str;
}

/**
 * DocumentPickerDrawer
 * Props:
 *   isOpen        - boolean
 *   onClose       - fn()
 *   onSelect      - fn(fileObj)  — called with selected DMS file meta
 *   panelLabel    - "Document 1" | "Document 2"
 */
const DocumentPickerDrawer = ({ isOpen, onClose, onSelect, panelLabel }) => {
  const [tab, setTab] = useState('my-docs');
  const [search, setSearch] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState('created');
  const [sortDir, setSortDir] = useState('desc');
  const [previewFile, setPreviewFile] = useState(null);
  const searchTimerRef = useRef(null);
  const LIMIT = 15;

  const fetchFiles = useCallback((overrides = {}) => {
    const params = {
      tab, page, limit: LIMIT, sortField, sortDir,
      ...(search ? { search } : {}),
      ...overrides,
    };
    const qs = new URLSearchParams(params).toString();
    setLoading(true);
    fetchMethodRequest('GET', `${apiCalls.comparePicker}?${qs}`)
      .then(res => {
        if (res?.files) {
          setFiles(res.files);
          setTotalCount(res.pagination?.totalCount || 0);
        }
      })
      .finally(() => setLoading(false));
  }, [tab, page, sortField, sortDir, search]);

  useEffect(() => {
    if (isOpen) fetchFiles();
  }, [isOpen, tab, page, sortField, sortDir]);

  // Debounce search
  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      if (isOpen) fetchFiles({ page: 1 });
      setPage(1);
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [search]);

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setPage(1);
    setPreviewFile(null);
  };

  const handleSelect = (file) => {
    onSelect({
      sourceType: 'dms',
      fileId: file._id,
      name: file.name || file.fileName,
      fileName: file.name || file.fileName,
      type: (file.name || file.fileName || '').split('.').pop()?.toLowerCase(),
      owner: file.createdByName || '—',
      path: file.path,
      modified: file.updated || file.created,
      oneLineSummery: file.oneLineSummery,
      parentFolderIdSearch: file.parentFolderIdSearch,
    });
    onClose();
  };

  const totalPages = Math.ceil(totalCount / LIMIT);

  return (
    <>
      {/* Overlay */}
      <div
        className={`dpd-overlay ${isOpen ? 'dpd-overlay--visible' : ''}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`dpd-drawer ${isOpen ? 'dpd-drawer--open' : ''}`}>
        {/* Header */}
        <div className="dpd-header">
          <div className="dpd-header-title">
            <i className="pi pi-folder-open me-2" />
            Select for <strong>{panelLabel}</strong>
          </div>
          <button className="dpd-close-btn" onClick={onClose} aria-label="Close picker">
            <i className="pi pi-times" />
          </button>
        </div>

        {/* Tabs */}
        <div className="dpd-tabs">
          <button
            className={`dpd-tab ${tab === 'my-docs' ? 'dpd-tab--active' : ''}`}
            onClick={() => handleTabChange('my-docs')}
          >
            <i className="pi pi-folder me-1" /> My Documents
          </button>
          <button
            className={`dpd-tab ${tab === 'shared' ? 'dpd-tab--active' : ''}`}
            onClick={() => handleTabChange('shared')}
          >
            <i className="pi pi-share-alt me-1" /> Shared With Me
          </button>
        </div>

        {/* Search + Sort */}
        <div className="dpd-toolbar">
          <div className="dpd-search-wrap">
            <i className="pi pi-search dpd-search-icon" />
            <input
              type="text"
              className="dpd-search"
              placeholder="Search files..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="dpd-search-clear" onClick={() => setSearch('')}>
                <i className="pi pi-times-circle" />
              </button>
            )}
          </div>
          <select
            className="dpd-sort-select"
            value={`${sortField}:${sortDir}`}
            onChange={e => {
              const [f, d] = e.target.value.split(':');
              setSortField(f);
              setSortDir(d);
              setPage(1);
            }}
          >
            <option value="created:desc">Newest First</option>
            <option value="created:asc">Oldest First</option>
            <option value="name:asc">Name A–Z</option>
            <option value="name:desc">Name Z–A</option>
          </select>
        </div>

        {/* Split: file list + preview */}
        <div className="dpd-body">
          {/* File List */}
          <div className="dpd-list-col">
            {loading ? (
              <div className="dpd-loading">
                <i className="pi pi-spin pi-spinner dpd-spinner" />
                <span>Loading files...</span>
              </div>
            ) : files.length === 0 ? (
              <div className="dpd-empty">
                <i className="pi pi-inbox dpd-empty-icon" />
                <p>No files found</p>
              </div>
            ) : (
              <div className="dpd-file-list">
                {files.map(file => {
                  const { icon, color } = getFileTypeInfo(file.name || file.fileName);
                  const isActive = previewFile?._id === file._id;
                  return (
                    <div
                      key={file._id}
                      className={`dpd-file-row ${isActive ? 'dpd-file-row--active' : ''}`}
                      onClick={() => setPreviewFile(file)}
                    >
                      <div className="dpd-file-icon-wrap">
                        <i className={icon} style={{ color, fontSize: '1.5rem' }} />
                      </div>
                      <div className="dpd-file-info">
                        <div className="dpd-file-name" title={file.name || file.fileName}>
                          {truncate(file.name || file.fileName, 34)}
                        </div>
                        <div className="dpd-file-meta">
                          <span className="dpd-badge dpd-badge--type">
                            {(file.name || file.fileName || '').split('.').pop()?.toUpperCase()}
                          </span>
                          <span className="dpd-file-owner">
                            <i className="pi pi-user" style={{ fontSize: '0.6rem' }} />
                            {' '}{file.createdByName || '—'}
                          </span>
                          <span className="dpd-file-date">
                            {formatDate(file.updated || file.created)}
                          </span>
                        </div>
                        {file.parentFolderIdSearch && (
                          <div className="dpd-file-path">
                            <i className="pi pi-folder me-1" style={{ fontSize: '0.6rem' }} />
                            {file.parentFolderIdSearch}
                          </div>
                        )}
                      </div>
                      <button
                        className="dpd-select-btn"
                        onClick={(e) => { e.stopPropagation(); handleSelect(file); }}
                        title="Select this file"
                      >
                        <i className="pi pi-check-circle" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="dpd-pagination">
                <button
                  className="dpd-page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <i className="pi pi-chevron-left" />
                </button>
                <span className="dpd-page-info">{page} / {totalPages}</span>
                <button
                  className="dpd-page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  <i className="pi pi-chevron-right" />
                </button>
              </div>
            )}
          </div>

          {/* Preview Pane */}
          {previewFile && (
            <div className="dpd-preview-col">
              <div className="dpd-preview-header">
                <i className={getFileTypeInfo(previewFile.name || previewFile.fileName).icon}
                  style={{ color: getFileTypeInfo(previewFile.name || previewFile.fileName).color, fontSize: '2rem' }}
                />
                <div>
                  <div className="dpd-preview-title">{previewFile.name || previewFile.fileName}</div>
                  <div className="dpd-preview-subtitle">
                    {previewFile.parentFolderIdSearch || 'Root'}
                  </div>
                </div>
              </div>

              <div className="dpd-preview-meta-grid">
                <div className="dpd-preview-meta-item">
                  <span className="dpd-preview-meta-label">Owner</span>
                  <span className="dpd-preview-meta-value">{previewFile.createdByName || '—'}</span>
                </div>
                <div className="dpd-preview-meta-item">
                  <span className="dpd-preview-meta-label">Modified</span>
                  <span className="dpd-preview-meta-value">{formatDate(previewFile.updated || previewFile.created)}</span>
                </div>
                <div className="dpd-preview-meta-item">
                  <span className="dpd-preview-meta-label">Type</span>
                  <span className="dpd-preview-meta-value">
                    {(previewFile.name || previewFile.fileName || '').split('.').pop()?.toUpperCase()}
                  </span>
                </div>
                {previewFile.tag && (
                  <div className="dpd-preview-meta-item">
                    <span className="dpd-preview-meta-label">Tag</span>
                    <span className="dpd-preview-meta-value">{previewFile.tag}</span>
                  </div>
                )}
              </div>

              {previewFile.oneLineSummery && (
                <div className="dpd-preview-summary">
                  <div className="dpd-preview-summary-label">Summary</div>
                  <div
                    className="dpd-preview-summary-text"
                    dangerouslySetInnerHTML={{ __html: marked(previewFile.oneLineSummery) }}
                  />
                </div>
              )}

              <Button
                label="Select This File"
                icon="pi pi-check"
                className="dpd-confirm-btn w-100 mt-3"
                onClick={() => handleSelect(previewFile)}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DocumentPickerDrawer;
